"use client";

import { ReactNode } from "react";
import { iHolidayQuoteX } from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import {
  ItinerarySummary,
  ItinerarySummaryVariant,
} from "../itinerary-summary";
import { useQuoteTheme } from "./use-quote-theme";

export interface QuotePageContentLayoutProps {
  quote: iHolidayQuoteX;
  itinerarySummaryVariant?: ItinerarySummaryVariant;
  pricingMode?: "grid" | "grid-asymmetric" | "sidebar";
  preItinerary: ReactNode;
  itinerary: ReactNode;
  postItinerary: ReactNode;
  pricing: ReactNode;
  postPricing: ReactNode;
}

export function QuotePageContentLayout({
  quote,
  itinerarySummaryVariant = "default",
  pricingMode = "grid",
  preItinerary,
  itinerary,
  postItinerary,
  pricing,
  postPricing,
}: QuotePageContentLayoutProps) {
  const { classNames } = useQuoteTheme();

  const pricingBlocks = (
    <div className="space-y-8">
      {pricing}
      {postPricing}
    </div>
  );

  if (pricingMode === "grid-asymmetric") {
    return (
      <div className="mx-auto max-w-screen-xl space-y-8 px-4 py-8">
        <ItinerarySummary
          itineraryBlocks={quote.itineraryBlocks}
          variant={itinerarySummaryVariant}
          className={classNames.itinerarySummary}
        />

        {preItinerary}
        {itinerary}
        {postItinerary}

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[7fr_13fr]">
          <div className="space-y-8">{pricing}</div>
          <div className="space-y-8">{postPricing}</div>
        </div>
      </div>
    );
  }

  if (pricingMode === "sidebar") {
    return (
      <div className="mx-auto grid max-w-screen-xl gap-8 px-4 py-8 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
        <aside className="space-y-8 lg:sticky lg:top-20 lg:self-start">
          <ItinerarySummary
            itineraryBlocks={quote.itineraryBlocks}
            variant={itinerarySummaryVariant}
            className={classNames.itinerarySummary}
          />
          {pricingBlocks}
        </aside>

        <div className="space-y-8">
          {preItinerary}
          {itinerary}
          {postItinerary}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-screen-xl space-y-8 px-4 py-8">
      <ItinerarySummary
        itineraryBlocks={quote.itineraryBlocks}
        variant={itinerarySummaryVariant}
        className={classNames.itinerarySummary}
      />

      {preItinerary}
      {itinerary}
      {postItinerary}

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div className="space-y-8">{pricing}</div>
        <div className="space-y-8">{postPricing}</div>
      </div>
    </div>
  );
}
